import { buildLookAheadFilters } from "../../../pages/TaskManagementPage/lookAheadFilters/lookAheadFilters";
import {
  MY_LOOK_AHEAD_PLAN_DATE_CHANGED,
  REGULARS,
  SCHEDULER_FILTER_DURATION_DRIVEN_BACKLOG_SELECTED,
  TASKS,
} from "../../../global/constants";
import { preferenceStorage } from "../../../services/user-preferences/preferenceStorage";
import { datesAreEquals } from "../../../utils/DateUtils/DateUtils";
import {
  getScheduleActivityCustomColumnsOfApprovedProjects,
  getUserExtendedRegulars,
} from "../../../services/schedule-activity-services/scheduleActivityActions";
import { getResourceTasks } from "../../../services/scheduler-services/schedulerActions";
import { WorkPackageSchedulerFilters } from "./WorkPackageScheduler/configs";
import { TaskManagementSchedulerFilters } from "./TaskManagement/configs";
import { recreateSchedulerInstance } from "../../../services/utils-services/utilsActions";
import { t } from "i18next";

export const computeBacklogStatusFromProgress = (progress, currentBacklogStatus) => {
  if (progress === 100) {
    return "DONE";
  }
  return currentBacklogStatus;
};

export const getEvents = (items, instance = TASKS, collaborators = []) => {
  if (instance === TASKS) return getTasksEvents(items);
  if (instance === REGULARS) return getRegularEvents(items, collaborators);
};

export const getAssignments = (items, instance = TASKS) => {
  if (instance === TASKS) return getTasksAssignments(items);
  if (instance === REGULARS) return getRegularAssignments(items);
};

const getTasksEvents = (tasks) => {
  return tasks.map((task) => ({
    ...task.resourceTask,
    id: task.resourceTask.id,
    name: task.resourceTask.name,
    startDate: task.resourceTask.start,
    endDate: task.resourceTask.finish,
    duration: task.resourceTask.duration,
    notes: task.resourceTask.notes,
    effort: task.resourceTask.work,
    inactive: !task.resourceTask.isActive,
    schedulingMode: task.taskType,
    eventColor: getEventColor(task.resourceTask),
    projectName: task.projectName,
    constraintType: "startnoearlierthan",
    constraintDate: task.resourceTask.start,
    readOnly: Boolean(task.readOnly),
  }));
};

const getEventColor = (task) => {
  const inConflict = Reflect.get(task, "inConflict");
  const isActive = Reflect.get(task, "isActive");
  // let color = inConflict ? "orange" : "green";  // TODO temp
  let color = "gray";
  if (isActive) {
    color = task.progressColor || "green";
  }
  return color;
};

const getTasksAssignments = (tasks) => {
  return tasks
    .filter((task) => !!task.resourceTask.resource?.id)
    .map((task, index) => ({
      id: index,
      resource: task.resourceTask.resource.id,
      event: task.resourceTask.id,
    }));
};

export const getTasksDependencies = (tasks) => {
  const deps = [];

  tasks
    .filter((task) => task.resourceTask.predecessors)
    .forEach((task) => {
      const predecessors = task.resourceTask.predecessors.split(", ");
      predecessors.forEach((predecessor) => {
        const newId = `dep_${predecessor}-${task.resourceTask.id}`;
        if (predecessor && !deps.find((d) => d.id === newId)) {
          deps.push({
            id: newId,
            fromEvent: +predecessor,
            toEvent: task.resourceTask.id,
          });
        }
      });
    });

  return deps;
};

const getRegularEvents = (regulars, collaborators) => {
  return regulars.map((task) => {
    const computedBacklogStatus = computeBacklogStatusFromProgress(
      task.scheduleActivity.progress,
      task.backlogStatus
    );
    
    return {
      ...task.scheduleActivity,
      id: task.scheduleActivity.id,
      name: task.scheduleActivity.name,
      startDate: task.scheduleActivity.startDate,
      endDate: task.scheduleActivity.finishDate,
      duration: task.scheduleActivity.duration,
      notes: task.scheduleActivity.notes,
      effort: task.scheduleActivity.work,
      inactive: !task.scheduleActivity.isActive,
      schedulingMode: "Normal", // unsure if it cover 100% cases
      eventColor: getBacklogColor(task.scheduleActivity),
      projectName: task.projectName,
      constraintType: "startnoearlierthan",
      constraintDate: task.scheduleActivity.startDate,
      projectId: task.projectId,
      structureId: task.structureId,
      scenarioId: task.scenarioId,
      //hasConflicts: task.hasConflicts,
      style: getConflictStatusStyle(task),
      saParentName: task.saParentName,
      backlogStatus: computedBacklogStatus,
      firstCollaborator: getFirstCollaborator(task, collaborators),
      readOnly: Boolean(task.readOnly),
    };
  });
};

const getFirstCollaborator = (task, collaborators) => {
  if (!collaborators?.length) return undefined;

  const teamsIds = Object.keys(
    task.scheduleActivity.ganttResourceAllocation || {}
  ).map(Number);

  const collaborator = collaborators.find((collaborator) => {
    const parentIds = collaborator.parentIds.split(",").map(Number);
    return parentIds.some((parentId) => teamsIds.includes(parentId));
  });

  return collaborator;
};

const getConflictStatusStyle = (task) => {
  return ""; // TODO temp
  // const style = "border: ";
  // const suffix = task.hasConflicts ? "4px dotted orange" : "none";
  // return `${style}${suffix}`;
};

const getBacklogColor = (task) => {
  const isActive = Reflect.get(task, "isActive");
  const { assignmentsCompleted, totalAssignments } = getNumberOfAssignment(
    task
  );

  if (!isActive) return "gray";
  if (assignmentsCompleted < totalAssignments) return "blue";
  if (assignmentsCompleted > totalAssignments) return "purple";

  return "green";
};

const getRegularAssignments = (regulars) => {
  const regularsArray = [];
  regulars.forEach((r) => {
    Object.keys(
      r.scheduleActivity.ganttResourceAllocation
    ).forEach((resource) =>
      regularsArray.push({ resource, event: r.scheduleActivity.id })
    );
  });
  return regularsArray.map((data, index) => ({
    id: index,
    resource: +data.resource,
    event: data.event,
  }));
};

export const reassessConflicts = (eventStore, data, instance) => {
  const updatedTasks = [...data.items];
  const property = getEventProperty(instance);
  updatedTasks.forEach((task) => {
    const rt = task[property];
    const event = eventStore.getById(rt.id);

    if (event && rt) {
      if (eventStore.project.id === "scenario-scheduler-project") {
        const start = new Date(rt.start);
        const finish = new Date(rt.finish);
        const startChanged =
          start.getTime() !== new Date(event.startDate).getTime();
        const finishChanged =
          finish.getTime() !== new Date(event.endDate).getTime();
        if (startChanged || finishChanged || event.duration !== rt.duration) {
          event.set("startDate", start);
          event.set("endDate", finish);
          event.set("duration", rt.duration);
        }
      } // we apply this only for the "top-down" future of the scenario scheduler

      const colorGetter =
        instance === REGULARS ? getBacklogColor : getEventColor;
      const color = colorGetter(rt);
      if (event.eventColor !== color) {
        event.set("eventColor", color);
      }

      event.set("customColumns", rt.customColumns);

      if (instance === REGULARS) {
        event.genericMetrics = { ...rt.genericMetrics };
      }
    }
  });
};

export const shouldUpdateData = (
  projectRef,
  newData,
  preservedTasks,
  instance
) => {
  const eventStore = projectRef.current.instance.eventStore;
  const records = eventStore.records;
  const property = getEventProperty(instance);

  const hasToAdd = records.some(
    (r) => "id" in r.data && typeof r.data.id === "string"
  );

  if (hasToAdd) {
    const itemToAdd = newData.items[newData.items.length - 1];
    reassessConflicts(eventStore, newData, instance);
    return { action: "add", data: getEvents([itemToAdd], instance) };
  } else if (preservedTasks.length < newData.items.length) {
    const itemsToAssign = newData.items.filter(
      (r) => !preservedTasks.some((d) => d[property].id === r[property].id)
    );
    reassessConflicts(eventStore, newData, instance);
    return { action: "assign", data: getEvents(itemsToAssign, instance) };
  } else if (preservedTasks.length > newData.items.length) {
    const itemsToDelete = preservedTasks
      .filter(
        (r) => !newData.items.some((d) => d[property].id === r[property].id)
      )
      .map((r) => r[property].id);

    const recordsToDelete = records.filter((r) => itemsToDelete.includes(r.id));
    reassessConflicts(eventStore, newData, instance);
    return { action: "remove", data: recordsToDelete };
  } else {
    const filteredTasks = filterTasks(
      newData.items,
      newData.resources,
      instance
    );
    reassessConflicts(eventStore, newData, instance);
    return { action: "update", data: { ...newData, items: filteredTasks } };
  }
};

export const updateDataHandler = {
  add: (project, data) => {
    const { eventStore } = project.current.instance;
    const recordsToRemove = eventStore.records.filter(
      (r) => (r.id && typeof r.id === "string") || !r.id
    );
    eventStore.remove(recordsToRemove);
    handleAssignmentDeletion(project, recordsToRemove);
    handleEventAdd(project, data);
  },
  assign: (project, data) => {
    handleEventAdd(project, data);
  },
  update: (project, data, instance) => {
    reassessConflicts(project.current.instance.eventStore, data, instance);
  },
  remove: (project, data) => {
    project.current.instance.eventStore.remove(data);
    handleAssignmentDeletion(project, data);
  },
};

const handleAssignmentDeletion = (project, data) => {
  const { assignmentStore } = project.current.instance;
  const ids = data.map((val) => val.id);
  const assignmentsToRemove = assignmentStore.records.filter((r) =>
    ids.includes(r.eventId)
  );
  assignmentStore.remove(assignmentsToRemove);
};

export const handleEventAdd = (project, data) => {
  const {
    assignmentStore,
    eventStore,
    resourceStore,
  } = project.current.instance;
  eventStore.add(data);
  data.forEach((item) => {
    const event = eventStore.getById(item.id);
    const resource = resourceStore.getById(item.resource?.id);
    if (event && resource) {
      assignmentStore.assignEventToResource(event, [resource]);
    }
  });
};

export const shouldUpdateResources = (preservedResources, dataSource) => {
  const length = dataSource?.resources.length;

  if (
    (!preservedResources.length && !length) ||
    preservedResources.length === length
  )
    return;

  if (preservedResources.length > length) {
    return { action: "filter" };
  } else if (preservedResources.length < length) {
    return { action: "add" };
  }
};

export const updateResourceHandler = {
  filter: (project, dataSource) => {
    const { resourceStore } = project.current.instance;
    resourceStore.clearFilters();
    const resourceIds = dataSource.resources.map((resource) => resource.id);
    resourceStore.filter({
      filters: (record) => resourceIds.includes(record.id),
      replace: false,
    });
  },
  add: (project, dataSource, preservedResources, preservedTasks, instance) => {
    const {
      resourceStore,
      eventStore,
      assignmentStore,
    } = project.current.instance;

    resourceStore.clearFilters();

    if (dataSource.resources.length !== resourceStore.records.length) {
      const property = getEventProperty(instance);

      const resourcesToAdd = dataSource.resources.filter(
        (resource) => !preservedResources.some((r) => r.id === resource.id)
      );
      const itemsToAdd = dataSource.items.filter(
        (task) =>
          !preservedTasks.some((d) => d[property].id === task[property].id)
      );

      resourceStore.add(resourcesToAdd);
      eventStore.add(getEvents(itemsToAdd));
      assignmentStore.add(getTasksAssignments(itemsToAdd));
    }
  },
};

export const isNotFiltering = (previousFilter, filter) =>
  previousFilter.every((collab) => filter.includes(collab)) &&
  filter.every((collab) => previousFilter.includes(collab));

export const filterTasks = (events = [], resources = [], instance) => {
  return events.filter(
    (event) => {
      if (instance === TASKS) {
        return resources.some(
          (c) =>
            event.resourceTask.resource &&
            c.id === event.resourceTask.resource.id
        );
      } else if (instance === REGULARS) {
        const eventResources = Object.keys(
          event.scheduleActivity.ganttResourceAllocation
        ).map((id) => +id);
        return resources.some((c) => eventResources.includes(c.id));
      }
    }
    // &&
    // (!selectedProjects.length || selectedProjects.includes(task.projectName))
  );
};

/*export default onTodayClick = (scheduler) => {
  const startDate = DateHelper.clearTime(new Date());
  startDate.setHours(7);
  //scheduler.current?.schedulerProInstance.startDate = startDate;
}*/

export const calculateTickSize = ({ viewPreset, startDate }) => {
  const visibleDays =
    viewPreset.displayDateFormat === "DD"
      ? 1
      : Math.ceil(
          viewPreset.timeResolution.getDurationInUnit(
            startDate,
            viewPreset.displayDateFormat
          ) / 24
        );
  const visibleMinutes = visibleDays * 24 * 60;
  const visibleHours = visibleMinutes / 60;

  if (visibleHours <= 12) {
    // If the visible range is 12 hours or less, use 15-minute intervals
    return 60;
  } else if (visibleHours <= 24) {
    // If the visible range is 24 hours or less, use 30-minute intervals
    return 60;
  } else if (visibleDays <= 7) {
    // If the visible range is 7 days or less, use 1-hour intervals
    return 24 * 60;
  } else {
    // For longer ranges, use 6-hour intervals
    return 24 * 60;
  }
};

const getEventProperty = (instance) => {
  if (instance === TASKS) return "resourceTask";
  if (instance === REGULARS) return "scheduleActivity";
};

export const updateSchedulerData = (
  project,
  dataSource,
  preservedResources,
  preservedTasks,
  instance = TASKS
) => {
  const updatingResources = shouldUpdateResources(
    preservedResources,
    dataSource
  );

  if (updatingResources) {
    const { action } = updatingResources;
    action in updateResourceHandler &&
      updateResourceHandler[action](
        project,
        dataSource,
        preservedResources,
        preservedTasks,
        instance
      );
  } else {
    const updatingData = shouldUpdateData(
      project,
      dataSource,
      preservedTasks,
      instance
    );

    if (updatingData) {
      const { action, data } = updatingData;
      action in updateDataHandler &&
        updateDataHandler[action](project, data, instance);
    }
  }
};

export const filterByProgress = (project) => {
  const eventStore = project.current.instance.eventStore;
  const filterId = WorkPackageSchedulerFilters.hideCompletedTasksFilter.id;
  const isFiltered = eventStore.filters.values.some((v) => v.id === filterId);

  if (isFiltered) {
    eventStore.removeFilter(filterId);
    return false;
  } else {
    eventStore.filter({
      id: filterId,
      filterBy: (record) => {
        return record.data.backlogStatus !== "DONE";
      },
    });
    return true;
  }
};

export const getNumberOfAssignment = (task) => {
  const { workAssigned, workAtCompletion } = task.genericMetrics;
  const dailyFTE = 7; // TODO : NEED TO BE PROVIDED FROM BACK END
  const oneAssignmentValue = dailyFTE * task.duration;
  if (oneAssignmentValue === 0) {
    return { assignmentsCompleted: 0, totalAssignments: 0 };
  }

  const assignmentsCompleted = Math.ceil(workAssigned / oneAssignmentValue);
  // SAs created without an explicit planned-work value have workAtCompletion=0.
  // Driving totalAssignments off workAtCompletion alone (and then clamping
  // assignmentsCompleted to 0 whenever total=0) hid every assignment made on
  // those SAs. Derive total from whichever of {planned, assigned} is larger,
  // so a freshly-assigned task shows e.g. 1/1 instead of 0/0.
  const totalAssignments = Math.ceil(
    Math.max(workAtCompletion, workAssigned) / oneAssignmentValue
  );

  return { assignmentsCompleted, totalAssignments };
};

export const getNumberOfAssignmentForUI = (task) => {
  if (!task.resources || !task.resources.length)
    return { assignmentsCompleted: 0, totalAssignments: 0 };

  const { assignmentsCompleted, totalAssignments } = getNumberOfAssignment(
    task.data
  );

  return `${assignmentsCompleted}/${totalAssignments}`;
};

export const filterWorkPackageSchedulerBySelectedRegulars = (
  project,
  selectedRegulars,
  isFilterBySelection
) => {
  const { resourceStore, eventStore } = project.current.instance;
  const resFilterId =
    WorkPackageSchedulerFilters.selectedRegularsForResourcesFilter.id;
  const evFilterId = WorkPackageSchedulerFilters.selectedRegularsFilter.id;

  if (selectedRegulars?.length && isFilterBySelection) {
    const selectedRegularIds = selectedRegulars.map((r) => r.id);
    let teamIds = [];
    selectedRegulars.forEach((r) => {
      const ids = Object.keys(r.ganttResourceAllocation).map((d) => +d) || [];
      teamIds = [...teamIds, ...ids];
    });

    eventStore.filter({
      id: evFilterId,
      filterBy: (event) => selectedRegularIds.includes(event.id),
    });
    resourceStore.filter({
      id: resFilterId,
      filterBy: (res) => teamIds.includes(res.id),
    });
  } else {
    const areResourcesFiltered = resourceStore.filters.values.some(
      (v) => v.id === resFilterId
    );
    const areEventsFiltered = eventStore.filters.values.some(
      (v) => v.id === evFilterId
    );

    if (areResourcesFiltered) resourceStore.removeFilter(resFilterId);
    if (areEventsFiltered) eventStore.removeFilter(evFilterId);
  }
};

export const filterWorkPackageSchedulerBySelectedTasks = (
  project,
  selectedTasks,
  isFilterBySelection
) => {
  const { resourceStore, eventStore } = project.current.instance;
  const resFilterId =
    WorkPackageSchedulerFilters.selectedTasksForResourcesFilter.id;
  const evFilterId = WorkPackageSchedulerFilters.selectedTasksFilter.id;

  if (selectedTasks?.length && isFilterBySelection) {
    const selectedRegularIds = selectedTasks
      .map((r) => r.parentSAId)
      .filter((id) => !!id);
    let teamIds = [];
    selectedTasks.forEach((t) => {
      const ids = t.resource.parentIds
        .split(",")
        .filter((d) => !!d)
        .map((id) => +id);
      teamIds = [...teamIds, ...ids];
    });

    eventStore.filter({
      id: evFilterId,
      filterBy: (event) => selectedRegularIds.includes(event.id),
    });
    resourceStore.filter({
      id: resFilterId,
      filterBy: (res) => teamIds.includes(res.id),
    });
  } else {
    const areResourcesFiltered = resourceStore.filters.values.some(
      (v) => v.id === resFilterId
    );
    const areEventsFiltered = eventStore.filters.values.some(
      (v) => v.id === evFilterId
    );

    if (areResourcesFiltered) resourceStore.removeFilter(resFilterId);
    if (areEventsFiltered) eventStore.removeFilter(evFilterId);
  }
};

export const sortResourcesByNameAndAssignments = (
  project,
  instance = TASKS
) => {
  const _tasks = project.current.instance.eventStore.records;
  let assignments = [];

  if (instance === TASKS) {
    assignments = [...new Set(_tasks.map((t) => t.data.resource.id))];
  } else if (instance === REGULARS) {
    _tasks.forEach((r) => {
      const ids = Object.keys(r.data.ganttResourceAllocation || {}).map(
        (id) => +id
      );
      assignments = [...assignments, ...ids];
    });
  }

  project.current.instance.resourceStore.sort((a, b) => {
    if (assignments.includes(a.id) && !assignments.includes(b.id)) {
      return -1;
    }
    if (!assignments.includes(a.id) && assignments.includes(b.id)) {
      return 1;
    }
    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });
};

export const sortResourcesByNameAndAssignmentsIfDurationDrivenBacklog = (
  project
) => {
  const _tasks = project.current.instance.eventStore.records;
  const assignments = [...new Set(_tasks.map((t) => t.data.resource.id))];

  const isDurationDrivenBacklogSelected =
    preferenceStorage("scheduler_filters").get(SCHEDULER_FILTER_DURATION_DRIVEN_BACKLOG_SELECTED, false) === true;

  project.current.instance.resourceStore.sort((a, b) => {
    if (isDurationDrivenBacklogSelected) {
      if (assignments.includes(a.id) && !assignments.includes(b.id)) {
        return -1;
      }
      if (!assignments.includes(a.id) && assignments.includes(b.id)) {
        return 1;
      }
    }

    if (a.name < b.name) return -1;
    if (a.name > b.name) return 1;
    return 0;
  });
};

const schedulerView = () => preferenceStorage("scheduler_view");

export const saveSettings = (schedulerId, property, value) => {
  const name = `${schedulerId}-settings`;
  schedulerView().set(name, { ...(schedulerView().get(name, null) || {}), [property]: value });
};

export const getProjectStartDate = () => {
  const date = new Date();
  date.setFullYear(date.getFullYear() - 10);
  return date;
};

export const reloadWorkPackageScheduler = (
  schedulerRef,
  isReady,
  shouldRegularsBeFiltered,
  selectedDatesRef,
  selectedProjects,
  selectedTeams,
  dispatch
) => {
  if (schedulerRef.current) {
    const project = schedulerRef.current.instance.project;
    schedulerRef.current.instance.mask("Loading...");

    shouldRegularsBeFiltered.current = project.eventStore.filters.values.some(
      (v) => v.id === WorkPackageSchedulerFilters.hideCompletedTasksFilter
    );

    Object.values(WorkPackageSchedulerFilters).forEach((filter) =>
      project[filter.store].removeFilter(filter.id)
    );

    project.eventStore.removeAll();
    project.resourceStore.removeAll();
    project.assignmentStore.removeAll();
    project.dependencyStore.removeAll();
  }

  const filterForSAs = buildLookAheadFilters(selectedDatesRef.current, selectedProjects, selectedTeams);
  const promices = Promise.all([
    dispatch(getUserExtendedRegulars(filterForSAs)),
    dispatch(
      getScheduleActivityCustomColumnsOfApprovedProjects(
        selectedDatesRef.current.start,
        selectedDatesRef.current.finish
      )
    ),
  ]);

  const finalizeReload = () => {
    if (schedulerRef.current) {
      schedulerRef.current.instance.unmask();
    }
    const event = new Event(MY_LOOK_AHEAD_PLAN_DATE_CHANGED);
    event.data = true;
    document.dispatchEvent(event);
  };
  promices.then(finalizeReload).catch(finalizeReload);

  isReady.current = false;

  return promices;
};

export const reloadTaskManagementScheduler = (
  schedulerRef,
  isReady,
  selectedDatesRef,
  dispatch
) => {
  if (!schedulerRef.current) return Promise.resolve();

  const project = schedulerRef.current.instance.project;
  const resourceUtilRef = schedulerRef.current.instance.partners.find(
    (p) => p.id === "TaskManagementResourceUtilization"
  );

  schedulerRef.current.instance.mask("Loading...");
  if (resourceUtilRef) resourceUtilRef.mask("Loading...");

  Object.values(TaskManagementSchedulerFilters).forEach((filter) =>
    schedulerRef.current.instance[filter.store].removeFilter(filter.id)
  );

  project.eventStore.removeAll();
  project.resourceStore.removeAll();
  project.assignmentStore.removeAll();
  project.dependencyStore.removeAll();

  if (resourceUtilRef) resourceUtilRef.store.removeAll();

  const promices = Promise.all([
    dispatch(getResourceTasks(selectedDatesRef.current)),
  ]);

  const finalizeReload = () => {
    if (schedulerRef.current) schedulerRef.current.instance.unmask();
    if (resourceUtilRef) resourceUtilRef.unmask();
    const event = new Event(MY_LOOK_AHEAD_PLAN_DATE_CHANGED);
    event.data = true;
    document.dispatchEvent(event);
  };
  promices.then(finalizeReload).catch(finalizeReload);

  isReady.current = false;

  return promices;
};

export const getStatusByProgress = (progress) => {
  if (progress === 0) {
    return "TODO";
  } else if (progress > 0 && progress < 90) {
    return "IN_PROGRESS";
  } else if (progress >= 90 && progress < 100) {
    return "REVIEW";
  } else if (progress === 100) {
    return "DONE";
  } else {
    return "TODO";
  }
};

export const setRegularToAnimate = (id) => {
  sessionStorage.setItem("regularToAnimate", id);
};

export const getRegularToAnimate = () => {
  return sessionStorage.getItem("regularToAnimate");
};

export const removeRegularToAnimate = () => {
  sessionStorage.removeItem("regularToAnimate");
};

export const getActivateTaskOnAssignValue = () =>
  schedulerView().get("activateTaskOnAssignValue", false) === true;

export const setActivateTaskOnAssignValue = (value) => {
  schedulerView().set("activateTaskOnAssignValue", Boolean(value));
};

export const animateNewItem = () => {
  const id = getRegularToAnimate();

  if (id) {
    const node = document.querySelectorAll(`.b-grid-row[data-id='${id}']`)[0];
    node?.classList.add("new-item-animation");
  }

  removeRegularToAnimate();
};

export const matchResourceWithCalendar = (resources, calendars) => {
  return resources.map((resource) => {
    const calendarName = calendars.find(
      (calendar) => calendar.id === resource.selectedCalendarId
    )?.name;
    return { ...resource, calendarName };
  });
};

export const toggleSchedulerMode = (
  schedulerId,
  isVertical = false,
  dispatch
) => {
  schedulerView().set(`${schedulerId}_vertical-mode`, Boolean(isVertical));
  dispatch(recreateSchedulerInstance(true));
  setTimeout(() => dispatch(recreateSchedulerInstance(false)), 10);
};

export const getSchedulerMode = (schedulerId, revert = false) => {
  let value = schedulerView().get(`${schedulerId}_vertical-mode`, false) === true;
  value = revert ? !value : value;
  return value ? "vertical" : "horizontal";
};

export const setPropertiesForVerticalView = (schedulerRef) => {
  schedulerRef.current.instance.resourceColumns.fitWidth = true;
  schedulerRef.current.instance.resourceColumns.columnWidth = 200;
  schedulerRef.current.instance.eventLayout = "pack";
  schedulerRef.current.instance.narrowEventWidth = 200;

  schedulerRef.current.instance.tbar.widgetMap.settingsButton.menu.add({
    text: t("global.settings.columnWidth"),
    type: "slider",
    showValue: true,
    value: schedulerRef.current.instance.resourceColumns.columnWidth,
    min: 200,
    max: 2000,
    onInput: ({ value }) => {
      schedulerRef.current.instance.resourceColumns.columnWidth = value;
    },
  });

  setTimeout(() => {
    schedulerRef.current.instance.resourceColumns.fitWidth = false;
    schedulerRef.current.instance.resourceColumns.fillWidth = true;
  }, 0); // workaround to make resource columns scrollable
};
export const mergeTaskWithCustomColumn = (tasks, customColumns) => {
  return tasks.map((task) => {
    const embedded = task.resourceTask.customColumns;
    const hasStoreEntry =
      getRawCustomColumnEntry(customColumns, task.resourceTask.id) !== undefined;

    // No store values yet for this row (the whole-interval request is slower
    // than the row stream): keep whatever the row DTO embedded until they land.
    // An unconditional embedded early-return here used to freeze rows whose DTO
    // embedded an empty/partial customColumns object — the late store values
    // were then never merged and the cells stayed blank until a full reload.
    if (!hasStoreEntry) {
      if (embedded !== undefined) return task;
      return {
        ...task,
        resourceTask: { ...task.resourceTask, customColumns: {} },
      };
    }

    // Store values are authoritative: user edits update the store entry
    // (ADD_RT_CUSTOM_COLUMNS / UPDATE_RT_CUSTOM_COLUMN_VALUE), embedded values
    // only cover the first paint. Keep the task reference stable when the
    // store adds nothing over what the row already carries.
    const values = getCustomColumnFieldsForEvent(
      customColumns,
      task.resourceTask.id
    );
    if (embedded !== undefined) {
      const valueKeys = Object.keys(values);
      const embeddedMatchesStore =
        valueKeys.length === Object.keys(embedded).length &&
        valueKeys.every((key) => embedded[key] === values[key]);
      if (embeddedMatchesStore) return task;
    }

    return {
      ...task,
      resourceTask: {
        ...task.resourceTask,
        customColumns: embedded ? { ...embedded, ...values } : values,
      },
    };
  });
};

// Per-array index for getCustomColumnFieldsForEvent. Callers invoke it once PER ROW inside
// render memos (WorkPackageTable, TaskFocusGanttWrapper, gantt/scheduler utils…), so the
// linear .find made every merge O(N²) — live-profiled as seconds of main thread per streamed
// chunk at ~2k rows. The index is keyed by the customColumns ARRAY reference: rebuilt once
// per Redux state version, O(1) lookups after that, garbage-collected with the array.
const customColumnsIndexCache = new WeakMap();
const getCustomColumnsIndex = (customColumns) => {
  let index = customColumnsIndexCache.get(customColumns);
  if (!index) {
    index = new Map();
    customColumns.forEach((column) => {
      if (column && column.id != null) index.set(+column.id, column);
    });
    customColumnsIndexCache.set(customColumns, index);
  }
  return index;
};

export const getCustomColumnFieldsForEvent = (customColumns, eventId) => {
  if (!Array.isArray(customColumns) || customColumns.length === 0) return {};
  let customColumnData = getCustomColumnsIndex(customColumns).get(+eventId);

  if (customColumnData?.id) {
    customColumnData = { ...customColumnData };
    delete customColumnData.id;
  }

  return customColumnData || {};
};

// RAW entry (reference-stable across state versions while the row's values are untouched —
// the reducers replace an entry object only when that SA's values change). Used as the
// per-row cache-invalidation token by the per-element transform memoizations: recompute a
// row's derived object only when ITS custom-column entry reference changed.
export const getRawCustomColumnEntry = (customColumns, eventId) => {
  if (!Array.isArray(customColumns) || customColumns.length === 0)
    return undefined;
  return getCustomColumnsIndex(customColumns).get(+eventId);
};

export const getCustomColumnFields = (customColumns) => {
  const columnFields = new Set();
  customColumns.forEach((data) => {
    const fields = Object.keys(data).filter((name) => name !== "id");
    fields.forEach((field) => {
      columnFields.add(field);
    });
  });
  return [...columnFields];
};

export const extractRTCustomColumnNames = (customColumns) =>
  customColumns.reduce((arr, column) => {
    if (!column.name.match(/unnamed\d/)) arr.push(column.name);
    return arr;
  }, []);
